import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiSearch,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";
import api from "../../utills/api";

const Admissions = () => {
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [leads, setLeads] = useState([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const getArray = (response) => {
    const data = response?.data?.data;

    if (Array.isArray(data)) return data;

    if (Array.isArray(data?.applications)) {
      return data.applications;
    }

    if (Array.isArray(data?.items)) {
      return data.items;
    }

    if (Array.isArray(data?.docs)) {
      return data.docs;
    }

    return [];
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const [applicationsResponse, leadsResponse] =
        await Promise.all([
          api.get("/applications"),
          api.get("/leads"),
        ]);

      setApplications(getArray(applicationsResponse));
      setLeads(getArray(leadsResponse));
    } catch (error) {
      console.error("Failed to load admissions:", error);
      setApplications([]);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const admittedApplications = useMemo(() => {
    return applications.filter(
      (application) =>
        String(application?.status || "").toLowerCase() ===
        "admitted"
    );
  }, [applications]);

  const pendingApplications = useMemo(() => {
    return applications.filter((application) =>
      ["submitted", "under review", "approved"].includes(
        String(application?.status || "").toLowerCase()
      )
    );
  }, [applications]);

  const rejectedApplications = useMemo(() => {
    return applications.filter(
      (application) =>
        String(application?.status || "").toLowerCase() ===
        "rejected"
    );
  }, [applications]);

  const admissionLeads = useMemo(() => {
    return leads.filter((lead) => {
      const status = String(lead?.status || "").toLowerCase();

      return (
        status === "converted" ||
        status === "admitted"
      );
    });
  }, [leads]);

  const filteredApplications = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return admittedApplications.filter((application) => {
      const name =
        application?.applicantName ||
        application?.studentName ||
        application?.lead?.name ||
        "";

      const applicationNumber =
        application?.applicationNumber ||
        application?.applicationNo ||
        application?.number ||
        "";

      const course =
        application?.course ||
        application?.program ||
        application?.courseName ||
        "";

      const matchesSearch =
        !keyword ||
        String(name).toLowerCase().includes(keyword) ||
        String(applicationNumber)
          .toLowerCase()
          .includes(keyword) ||
        String(course).toLowerCase().includes(keyword);

      const status =
        String(application?.status || "").toLowerCase();

      const matchesStatus =
        statusFilter === "All" ||
        status === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [
    admittedApplications,
    search,
    statusFilter,
  ]);

  const getStudentName = (application) =>
    application?.applicantName ||
    application?.studentName ||
    application?.lead?.name ||
    "Unnamed Student";

  const getApplicationNumber = (application) =>
    application?.applicationNumber ||
    application?.applicationNo ||
    application?.number ||
    "—";

  const getCourse = (application) =>
    application?.course ||
    application?.program ||
    application?.courseName ||
    "—";

  const getAdmissionDate = (application) =>
    application?.admissionDate ||
    application?.admittedAt ||
    application?.updatedAt ||
    application?.createdAt;

  const formatDate = (date) => {
    if (!date) return "—";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return "—";
    }

    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatStatus = (status) => {
    if (!status) return "—";

    return String(status)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="mb-1">Admissions</h4>
            <p className="text-muted mb-0">
              Manage admitted students and admission progress.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-light"
            onClick={loadData}
            disabled={loading}
          >
            <i className="ri-refresh-line me-1" />
            Refresh
          </button>
        </div>

        {/* Summary */}
        <div className="row">

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <FiUserCheck />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Total Admissions
                    </p>

                    <h4 className="mb-0">
                      {admittedApplications.length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <FiCheckCircle />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Admitted Applications
                    </p>

                    <h4 className="mb-0">
                      {admittedApplications.length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <FiClock />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      In Progress
                    </p>

                    <h4 className="mb-0">
                      {pendingApplications.length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-animate">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <FiUsers />
                    </span>
                  </div>

                  <div className="flex-grow-1 ms-3">
                    <p className="text-uppercase fw-medium text-muted mb-1">
                      Converted Leads
                    </p>

                    <h4 className="mb-0">
                      {admissionLeads.length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Admission List */}
        <div className="card">

          <div className="card-header">
            <div className="row align-items-center g-3">

              <div className="col-md-4">
                <div className="search-box">
                  <input
                    type="text"
                    className="form-control search"
                    placeholder="Search student, application or course..."
                    value={search}
                    onChange={(e) =>
                      setSearch(e.target.value)
                    }
                  />

                  <FiSearch className="search-icon" />
                </div>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value)
                  }
                >
                  <option value="All">
                    All Admissions
                  </option>
                  <option value="Admitted">
                    Admitted
                  </option>
                </select>
              </div>

              <div className="col-md-5 text-md-end">
                <span className="text-muted">
                  Showing{" "}
                  <strong>
                    {filteredApplications.length}
                  </strong>{" "}
                  admissions
                </span>
              </div>

            </div>
          </div>

          <div className="card-body">

            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-primary"
                  role="status"
                >
                  <span className="visually-hidden">
                    Loading...
                  </span>
                </div>

                <p className="text-muted mt-3 mb-0">
                  Loading admissions...
                </p>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="text-center py-5">

                <div className="avatar-lg mx-auto mb-3">
                  <div className="avatar-title bg-light text-muted rounded-circle fs-1">
                    <FiUserCheck />
                  </div>
                </div>

                <h5>No admissions found</h5>

                <p className="text-muted mb-0">
                  No admitted applications match your
                  current filters.
                </p>

              </div>
            ) : (
              <div className="table-responsive">

                <table className="table table-hover align-middle mb-0">

                  <thead className="table-light">
                    <tr>
                      <th>Student</th>
                      <th>Application No.</th>
                      <th>Program / Course</th>
                      <th>Admission Date</th>
                      <th>Status</th>
                      <th className="text-end">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {filteredApplications.map(
                      (application) => {
                        const id =
                          application?._id ||
                          application?.id;

                        return (
                          <tr key={id}>

                            <td>
                              <div className="d-flex align-items-center">

                                <div className="avatar-xs me-2">
                                  <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                    {getStudentName(
                                      application
                                    )
                                      .charAt(0)
                                      .toUpperCase()}
                                  </span>
                                </div>

                                <div>
                                  <h6 className="mb-0">
                                    {getStudentName(
                                      application
                                    )}
                                  </h6>

                                  {application?.email && (
                                    <small className="text-muted">
                                      {application.email}
                                    </small>
                                  )}
                                </div>

                              </div>
                            </td>

                            <td>
                              <span className="fw-medium">
                                {getApplicationNumber(
                                  application
                                )}
                              </span>
                            </td>

                            <td>
                              {getCourse(application)}
                            </td>

                            <td>
                              {formatDate(
                                getAdmissionDate(
                                  application
                                )
                              )}
                            </td>

                            <td>
                              <span className="badge bg-success-subtle text-success">
                                {formatStatus(
                                  application?.status
                                )}
                              </span>
                            </td>

                            <td className="text-end">

                              <button
                                type="button"
                                className="btn btn-sm btn-soft-primary"
                                onClick={() =>
                                  navigate(
                                    `/applications/${id}`
                                  )
                                }
                              >
                                View
                                <FiArrowRight
                                  className="ms-1"
                                  size={14}
                                />
                              </button>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default Admissions;